import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { transactionApiServices } from "@services/apis/Transactions";
import { ValidateTransactionDetialsRequest } from "@services/models/Transactions/ValidateTransactionDetails";
import { useState } from "react";

interface MissingTransactionProps {
  isOpen: boolean;
  onClose: () => void;
  txnOrderId: string;
  network: string;
}

type ValidationState = "none" | "success" | "error";

const MissingTransactionSlider = ({
  isOpen,
  onClose,
  txnOrderId,
  network,
}: MissingTransactionProps) => {
  const [transactionHash, setTransactionHash] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [transactionValidated, setTransactionValidated] =
    useState<ValidationState>("none");
  const [message, setMessage] = useState<string>("");

  const handleOnClick = async () => {
    try {
      setLoading(true);
      const requestData: ValidateTransactionDetialsRequest = {
        transactionHash: transactionHash,
        network: network,
        txOID: txnOrderId,
      };
      const validateTransactionDetail =
        await transactionApiServices.validateTransactionDetails(requestData);
      if (validateTransactionDetail.status.toLowerCase() === "success") {
        setTransactionValidated("success");
      } else {
        setTransactionValidated("error");
      }
      setMessage(validateTransactionDetail.msg);
    } catch (error: any) {
      setTransactionValidated("error");
      setMessage(error?.response?.data?.msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlidingPanel
      showConfirm={false}
      isOpen={isOpen}
      isSlider={false}
      isMobile={false}
      heading={"Find missing transactions"}
      onClose={() => {
        onClose();
        setTransactionValidated("none");
        setTransactionHash("");
      }}
    >
      {transactionValidated !== "none" ? (
        <div className="flex flex-col gap-[16px] p-[16px]">
          <div
            className={`${
              transactionValidated === "success"
                ? "bg-bg-status-green"
                : "bg-bg-status-red"
            } rounded-lg flex flex-col items-center p-[16px]`}
          >
            <span className="text-[14px] text-text-base font-medium">
              {transactionValidated === "success"
                ? "Transaction found"
                : "Transaction not found"}
            </span>
            <div className="bg-[#15182C] rounded-full w-[40px] h-[40px] mt-[20px] flex items-center justify-center ">
              {transactionValidated === "success" ? (
                <span className="material-symbols-outlined text-bg-page-body">
                  check
                </span>
              ) : (
                <span className="material-symbols-outlined text-bg-page-body">
                  close
                </span>
              )}
            </div>
            <span className="text-[12px] text-text-base font-normal text-center mt-[20px]">
              {message ||
                (transactionValidated === "success"
                  ? "Based on the details submitted, the following transaction has been found and added to your wallet."
                  : "Based on the details submitted, no transaction has been found. Please recheck the details and submit again.")}
            </span>
          </div>
        </div>
      ) : (
        <div className="flex flex-col p-[16px] gap-[16px] overflow-y-auto">
          <span className="text-[12px] font-medium text-text-base">
            Submit the details below to locate your missing transactions.
          </span>
          <GlobalTextInputField
            type="text"
            title="Transaction hash"
            placeholder="Enter transaction hash here"
            value={transactionHash}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTransactionHash(e.target.value)
            }
          />
          <CustomButton
            isActive={true}
            isButtonLoading={loading}
            onClick={() => handleOnClick()}
            text="FIND TRANSACTION"
          />
        </div>
      )}
    </SlidingPanel>
  );
};

export default MissingTransactionSlider;
